import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { DailyResource } from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/dailyResource';
import { PlanDay } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { SectionChip } from './SectionChip';

const STRINGS = appStrings.modules.satActBooster.dailyPractice.studyTargets;

type StudyTargetsProps = Readonly<{
  day: PlanDay;
  /** The day's resolved resource; the timed instruction takes its section from this so the two agree. */
  resource: DailyResource | null;
}>;

/**
 * What the day asks of the student: its targets, each a section badge + category pill over its skill
 * standard, when one resolved. Timed and test days resolve no targets, so they carry a standing
 * instruction instead — without it they fall through to the no-targets line and read as a day off.
 */
export const StudyTargets = ({ day, resource }: StudyTargetsProps) => {
  const { targets } = day;
  const isTestDay = day.kind === 'test_day';
  const instruction =
    day.kind === 'timed'
      ? resource
        ? STRINGS.timed(resource.section)
        : STRINGS.timedNoResource
      : isTestDay
        ? STRINGS.testDay
        : null;

  return (
    <Box sx={rootSx} id="booster-study-targets">
      <Typography variant="display-xs-semibold" sx={titleSx}>
        {isTestDay ? STRINGS.testDayTitle : STRINGS.title}
      </Typography>

      {instruction && (
        <Typography variant="text-md-regular" sx={instructionSx}>
          {instruction}
        </Typography>
      )}

      {instruction === null && targets.length === 0 && (
        <Typography variant="text-md-regular" sx={emptySx}>
          {STRINGS.empty}
        </Typography>
      )}

      {targets.map((target, index) => {
        // The engine may echo the section as the category (e.g. "Math"/"Math"); show the category
        // label only when it adds something beyond the section badge.
        const showCategory =
          target.category.trim().toLowerCase() !== target.section.trim().toLowerCase();

        return (
          <Box key={`${target.section}-${target.category}-${index}`} sx={targetSx}>
            <Box sx={badgeGroupSx}>
              <SectionChip section={target.section} />
              {showCategory && (
                <Typography variant="text-sm-medium" sx={categorySx}>
                  {target.category}
                </Typography>
              )}
            </Box>
            {target.skillText && (
              <Typography variant="text-xl-regular" sx={skillSx}>
                {target.skillText}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const rootSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const emptySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const instructionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const targetSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

// The section badge and category label share one bordered pill.
const badgeGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignSelf: 'flex-start',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  paddingLeft: backrs.spacing['spacing-xs'],
  paddingRight: backrs.spacing['spacing-md'],
  paddingBlock: backrs.spacing['spacing-xs'],
  borderRadius: backrs.radius['radius-lg'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
});

const categorySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  whiteSpace: 'nowrap',
});

const skillSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
